/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { tryDecodeJwt } from 'modules/common/helpers/jwt';
import { SLICE } from 'modules/dashboard/constants';
import { signInActions } from 'modules/sign-in/slice';
import { usersActions } from 'modules/users/slice';
import { verifyCallbackActions } from 'modules/verify-callback/slice';

export const initialState = {
  user: {},
  organization: {},
  schemaMapping: {},
  tokens: {},
  loading: false,
  resetPasswordToken: '',
  sessionId: '',
};
/**
 * All actions related to auth feature are defined here
 */
export const authSlice = createSlice({
  name: SLICE.BASE_AUTH,
  initialState,
  reducers: {
    fetchUser(state) {
      state.loading = true;
    },
    fetchUserSucceeded(state, action) {
      state.loading = false;
      state.user = action?.payload;
    },
    fetchUserFailed(state) {
      state.loading = false;
    },
    getOrganization(state) {
      state.loading = true;
    },
    getOrganizationSucceeded(state, action) {
      state.loading = false;
      state.organization = action?.payload;
    },
    getOrganizationFailed(state) {
      state.loading = false;
    },
    getSchemaMapping(state) {
      state.loading = true;
    },
    getSchemaMappingSucceeded(state, action) {
      state.loading = false;
      state.schemaMapping = action?.payload;
    },
    getSchemaMappingFailed(state) {
      state.loading = false;
    },
    updateOrganization(state) {
      state.loading = true;
    },
    updateOrganizationSucceeded(state, action) {
      state.loading = false;
      state.organization = action?.payload;
    },
    updateOrganizationFailed(state) {
      state.loading = false;
    },
    signOut(state) {
      state.loading = true;
    },
    signOutSucceeded(state) {
      state.loading = false;
      state.user = {};
      state.tokens = {};
    },
    signOutFailed(state) {
      state.loading = false;
      state.user = {};
      state.tokens = {};
    },
    refreshToken(state) {
      state.loading = true;
    },
    refreshTokenSucceeded(state, action) {
      state.loading = false;
      state.tokens = action?.payload;
      state.sessionId = tryDecodeJwt(action?.payload?.accessToken)?.sid ?? '';
    },
    refreshTokenFailed(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(signInActions.signInSucceeded, (state, action) => ({
        ...state,
        tokens: action?.payload?.tokens,
        user: action?.payload?.user,
        sessionId: tryDecodeJwt(action?.payload?.tokens?.accessToken)?.sid ?? '',
      }))
      .addCase(signInActions.signInFailed, (state) => ({
        ...state,
        tokens: {},
        user: {},
      }))
      .addCase(verifyCallbackActions.verifyTokenSucceeded, (state, action) => ({
        ...state,
        resetPasswordToken: action?.payload?.token,
      }))
      .addCase(verifyCallbackActions.verifyTokenFailed, (state) => ({
        ...state,
        resetPasswordToken: '',
      }))
      .addCase(usersActions.updatePreferenceSucceeded, (state, action) => ({
        ...state,
        user: action?.payload,
      }))
      .addCase(usersActions.verifyOtpSucceeded, (state, action) => ({
        ...state,
        user: action?.payload?.user,
      }));
  },
});
//
export const { actions: authActions } = authSlice;
